import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './subscribe.css';

function authHeaders() {
  const token = localStorage.getItem('adeeb-token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const TIERS = [
  {
    id: 'standard',
    name: 'Standard',
    price: '£7.50',
    period: '/ month',
    profiles: 'Up to 3 profiles',
    features: [
      'Full library access',
      'Audio narration with synced highlighting',
      'Quizzes, achievements & rewards',
      'Cancel any time',
    ],
  },
  {
    id: 'family',
    name: 'Family',
    price: '£10',
    period: '/ month',
    profiles: 'Up to 6 profiles',
    badge: 'Best value',
    features: [
      'Everything in Standard',
      'Up to 6 reader profiles for the family',
      'Each child gets their own progress, favourites & rewards',
      'Cancel any time',
    ],
  },
];

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9.5" />
    <line x1="12" y1="11" x2="12" y2="16.5" />
    <circle cx="12" cy="7.5" r="0.4" fill="currentColor" />
  </svg>
);

const TickIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="5 12 10 17 19 7" />
  </svg>
);

const WarnIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l10 18H2L12 3z" />
    <line x1="12" y1="10" x2="12" y2="14" />
    <circle cx="12" cy="17.5" r="0.4" fill="currentColor" />
  </svg>
);

export default function Subscribe() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyTier, setBusyTier] = useState(null);
  const [error, setError] = useState('');

  const params = new URLSearchParams(location.search);
  const cancelled = params.get('subscription') === 'cancelled';
  const next = params.get('next') || '/profile';

  useEffect(() => {
    if (!localStorage.getItem('adeeb-token')) {
      navigate(`/register?redirect=${encodeURIComponent('/subscribe' + location.search)}`, { replace: true });
      return;
    }
    axios
      .get('/api/billing/status', { headers: authHeaders() })
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [navigate, location.search]);

  const handleSubscribe = async (tierId) => {
    setBusyTier(tierId);
    setError('');
    try {
      const { data } = await axios.post(
        '/api/billing/checkout',
        { next, tier: tierId },
        { headers: authHeaders() }
      );
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not start checkout.');
      setBusyTier(null);
    }
  };

  const handleManage = async () => {
    setBusyTier('manage');
    setError('');
    try {
      const { data } = await axios.post('/api/billing/portal', {}, { headers: authHeaders() });
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not open billing portal.');
      setBusyTier(null);
    }
  };

  if (loading) {
    return (
      <div className="sub-page">
        <div className="sub-loading">
          <div className="sub-spinner" />
          <p>Loading your subscription…</p>
        </div>
      </div>
    );
  }

  const isActive = status && status.active;

  // Active subscribers see the existing single card — they're not picking
  // a tier, they're managing the one they have. The two-card picker is
  // only for the unsubscribed flow.
  if (isActive) {
    return (
      <div className="sub-page">
        <div className="sub-shell">
          <Link to="/profiles/select" className="sub-back">← Back to profiles</Link>
          <div className="sub-card">
            <div className="sub-eyebrow">Deenlit Premium</div>
            <h1 className="sub-title">You're subscribed</h1>
            <div className="sub-notice is-active">
              <div className="sub-notice-row">
                <span className="sub-notice-icon"><TickIcon /></span>
                <div>
                  <span className="sub-notice-strong">Plan: {status.status}</span>
                  {status.currentPeriodEnd && (
                    <>Renews on {new Date(status.currentPeriodEnd).toLocaleDateString()}.</>
                  )}
                </div>
              </div>
            </div>
            <button onClick={handleManage} disabled={busyTier === 'manage'} className="sub-cta">
              {busyTier === 'manage' ? 'Opening billing portal…' : 'Manage subscription'}
            </button>
            {error && (
              <div className="sub-notice is-error" style={{ marginTop: 14, marginBottom: 0 }}>
                <div className="sub-notice-row">
                  <span className="sub-notice-icon"><WarnIcon /></span>
                  <div>{error}</div>
                </div>
              </div>
            )}
            <div className="sub-fineprint">
              Secured by Stripe · We never store your card details
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sub-page">
      <div className="sub-shell sub-shell--wide">
        <Link to="/profiles/select" className="sub-back">← Back to profiles</Link>

        <header className="sub-hero">
          <div className="sub-eyebrow">Deenlit Premium</div>
          <h1 className="sub-title">Pick a plan that suits your family</h1>
          <p className="sub-blurb">
            Audio narration, quizzes and the full library — for one quiet
            monthly price. Cancel any time.
          </p>
        </header>

        {cancelled && (
          <div className="sub-notice is-cancelled" style={{ maxWidth: 540, margin: '0 auto 8px' }}>
            <div className="sub-notice-row">
              <span className="sub-notice-icon"><InfoIcon /></span>
              <div>
                <span className="sub-notice-strong">Checkout cancelled</span>
                No charge was made — you can subscribe whenever you're ready.
              </div>
            </div>
          </div>
        )}

        <div className="sub-tier-grid">
          {TIERS.map((tier) => {
            const available = status?.tiers?.[tier.id] !== false;
            const isPopular = tier.id === 'family';
            return (
              <div key={tier.id} className={`sub-tier${isPopular ? ' is-popular' : ''}${!available ? ' is-unavailable' : ''}`}>
                {tier.badge && available && <div className="sub-tier-badge">{tier.badge}</div>}
                {!available && <div className="sub-tier-badge sub-tier-badge--soon">Coming soon</div>}
                <div className="sub-tier-name">{tier.name}</div>
                <div className="sub-tier-price">
                  <span className="sub-tier-price-amount">{tier.price}</span>
                  <span className="sub-tier-price-period">{tier.period}</span>
                </div>
                <div className="sub-tier-profiles">{tier.profiles}</div>
                <ul className="sub-tier-features">
                  {tier.features.map((f) => (
                    <li key={f}>
                      <span className="sub-features-check"><TickIcon /></span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => available && handleSubscribe(tier.id)}
                  disabled={!available || !!busyTier}
                  className={`sub-cta ${isPopular ? 'sub-cta-primary' : 'sub-cta-secondary'}`}
                  aria-disabled={!available}
                >
                  {!available
                    ? 'Coming soon'
                    : busyTier === tier.id
                      ? 'Redirecting to Stripe…'
                      : `Choose ${tier.name}`}
                </button>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="sub-notice is-error" style={{ maxWidth: 540, margin: '12px auto 0' }}>
            <div className="sub-notice-row">
              <span className="sub-notice-icon"><WarnIcon /></span>
              <div>{error}</div>
            </div>
          </div>
        )}

        <div className="sub-fineprint">
          Secured by Stripe · We never store your card details
        </div>

        {cancelled && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Link to="/profiles/select" className="sub-secondary" style={{ display: 'inline-block', width: 'auto', padding: '10px 18px' }}>
              Maybe later
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
